import { createEffect, createSignal, For, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { type AdminGoalBoard, renameAdminGoal, useAdminGoals } from '../lib/admin';
import { filterGoals, type GoalFilterKey } from '../lib/goal-view';
import type { OperatorRefusal } from '../lib/operator-credential';
import { absolute } from '../lib/time';
import { EmptyState } from './EmptyState';
import { GoalRow } from './GoalRow';
import { GoalSummaryBand } from './GoalSummaryBand';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// The autonomous goal monitor — developerz.ai's own roadmap, graded from live
// GitHub state (`GET /v1/admin/goals`). Container: it reads, the presenters render.
//
// A failed read surfaces as the boundary's ERROR, never as an empty list. The API
// answers 503 naming why the board is unavailable (no App credentials, the repo
// has no live installation, GitHub is down) precisely so this page can never say
// "we have no goals" when it means "the read failed" — this monitor's whole value
// is being trusted about whether work is moving.
//
// The source line carries `observedAt` because these counts are a SNAPSHOT, not a
// live feed: the query caches, a laptop sleeps, and a board about whether work is
// moving must never let a reader mistake an hour-old read for the current state.

export function AdminGoalMonitor(props: { token: string | null }): JSX.Element {
  const board = useAdminGoals(props.token);
  // Client-side, because the WHOLE board is already in this payload: a filter
  // that went back to the API would re-spend eleven GitHub calls to receive the
  // same rows and drop some. It is also why the narrow is deliberately NOT in
  // the URL — a shared link would then pin a reader to a subset of a board that
  // has since moved, which is the opposite of what this page is for.
  const [filter, setFilter] = createSignal<GoalFilterKey | null>(null);
  // A rename rides an `admin:goals` operator credential (#5724). A door refusal
  // sends nothing and is said once, above the list (with the sign-in action);
  // the row gets it as a FAILED rename, so its editor stays open over the draft.
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);
  const shown = (goals: AdminGoalBoard['goals']): AdminGoalBoard['goals'] =>
    filterGoals(goals, filter());

  // Applying a filter animates the LIST, not a remount of it.
  //
  // The first version keyed the list on the filter so it remounted, which
  // replayed a per-row CSS animation — and disposed every `GoalRow` with it,
  // silently discarding a rename draft someone was part-way through typing.
  // Losing a person's work to buy an effect is not a trade worth making, so the
  // container animates in place and the rows keep their state.
  //
  // The motion itself is the stylesheet's (`.goal-list--refiltered`,
  // `views/_goals.scss`): the house easing, behind the `reduce-motion` mixin,
  // so one preference governs it and no duration lives in TypeScript. The
  // element PERSISTS, and a CSS animation does not replay on an element that
  // keeps its class, so the class comes off, a reflow commits that, and it goes
  // back on — the replay, without a remount.
  let listEl: HTMLUListElement | undefined;
  createEffect(
    on(
      filter,
      () => {
        if (listEl === undefined) return;
        listEl.classList.remove('goal-list--refiltered');
        void listEl.offsetWidth;
        listEl.classList.add('goal-list--refiltered');
      },
      { defer: true },
    ),
  );

  return (
    <>
      <PageHeader title={t('goals.title')} desc={t('goals.subtitle')} />
      <OperatorRefusalAlert refusal={refusal()} />

      <QueryBoundary query={board} loading={<Skeleton height="8rem" />}>
        {(data) => (
          <>
            <GoalSummaryBand board={data} active={filter()} onFilter={setFilter} />

            <Panel title={t('goals.heading')} footer={t('goals.heading_desc')}>
              <Show when={filter() !== null}>
                {/* The count, whenever a narrow is on. `Open items` counts ITEMS
                    while every filter selects GOALS, so its tile can read 25 and
                    reveal 6 rows; saying "6 of 9" makes that arithmetic visible
                    instead of leaving it to look like a dropped row. */}
                <p class="goal-source">
                  {t('goals.showing', {
                    shown: String(shown(data.goals).length),
                    total: String(data.goals.length),
                  })}
                </p>
              </Show>

              <Show
                when={shown(data.goals).length > 0}
                fallback={
                  <EmptyState
                    message={filter() === null ? t('goals.empty') : t('goals.empty_filtered')}
                  />
                }
              >
                <ul class="goal-list" ref={listEl}>
                  <For each={shown(data.goals)}>
                    {(goal) => (
                      <GoalRow
                        goal={goal}
                        onRename={async (id, title) => {
                          setRefusal(null);
                          const out = await renameAdminGoal(id, title, props.token);
                          if (out.kind !== 'ok') {
                            setRefusal(out.kind);
                            throw new Error(`operator credential refused: ${out.kind}`);
                          }
                          // REFETCH, never a local splice. Every number on this
                          // page is supposed to have come from GitHub; a title
                          // patched in here would be the one field that did not,
                          // and it would survive a failed write silently.
                          await board.refetch();
                        }}
                      />
                    )}
                  </For>
                </ul>
              </Show>
              <p class="goal-source">
                {t('goals.source', { repo: data.repo, at: absolute(data.observedAt) })}
              </p>
            </Panel>
          </>
        )}
      </QueryBoundary>
    </>
  );
}
